// Title-screen / toggle UI widgets, drawn with vector Graphics like the cards.
// Kept out of game.ts so the orchestrator stays focused.
import { BlurFilter, Container, FillGradient, Graphics, Text } from "pixi.js";
import { palette, FONT } from "./theme";
import { Ease, scaleTo } from "./juice";
import type { Op } from "./problem";

/** A toggleable operator chip (also used for the "All" control). */
export class Chip extends Container {
  readonly op: Op | "all";
  selected = false;
  onTap?: () => void;

  private readonly w: number;
  private readonly h: number;
  private bg = new Graphics();
  private glow = new Graphics();
  private labelText: Text;

  constructor(text: string, op: Op | "all", w: number, h: number, fontSize: number) {
    super();
    this.op = op;
    this.w = w;
    this.h = h;
    this.pivot.set(w / 2, h / 2);

    this.glow.roundRect(-5, -5, w + 10, h + 10, 16).fill(palette.glow);
    this.glow.alpha = 0;
    this.addChild(this.glow);
    this.addChild(this.bg);

    this.labelText = new Text({
      text,
      style: { fontFamily: FONT, fontWeight: "700", fontSize, fill: palette.textDim },
    });
    this.labelText.anchor.set(0.5);
    this.labelText.position.set(w / 2, h / 2);
    this.addChild(this.labelText);

    this.eventMode = "static";
    this.cursor = "pointer";
    this.on("pointerover", () => scaleTo(this, 1.1, 140, Ease.outBack));
    this.on("pointerout", () => scaleTo(this, 1, 170, Ease.outCubic));
    this.on("pointertap", () => this.onTap?.());

    this.redraw();
  }

  setSelected(on: boolean) {
    if (on === this.selected) return;
    this.selected = on;
    this.redraw();
  }

  private redraw() {
    this.bg.clear();
    if (this.selected) {
      this.bg
        .roundRect(0, 0, this.w, this.h, 14)
        .fill({ color: palette.glow, alpha: 0.92 })
        .stroke({ width: 3, color: 0xffffff, alpha: 0.6 });
      this.labelText.style.fill = palette.ink;
      this.glow.alpha = 0.5;
    } else {
      this.bg
        .roundRect(0, 0, this.w, this.h, 14)
        .fill({ color: 0x06121a, alpha: 0.5 })
        .stroke({ width: 2, color: palette.slotEdge, alpha: 0.4 });
      this.labelText.style.fill = palette.textDim;
      this.glow.alpha = 0;
    }
  }
}

/** Big "START" button: a golden body that glows and sweeps a sheen, brighter on hover. */
export class StartButton extends Container {
  onActivate?: () => void;

  private readonly w: number;
  private readonly h: number;
  private glow = new Graphics();
  private body = new Graphics();
  private shine = new Graphics();
  private shineMask = new Graphics();
  private labelText: Text;
  private hovered = false;
  private enabled = true;

  constructor(w = 320, h = 96) {
    super();
    this.w = w;
    this.h = h;
    this.pivot.set(w / 2, h / 2);

    this.glow.roundRect(-24, -24, w + 48, h + 48, 34).fill(palette.gold);
    this.glow.alpha = 0.3;
    this.addChild(this.glow);

    // gold body with a top-down gradient for depth
    try {
      const grad = new FillGradient({
        type: "linear",
        start: { x: 0, y: 0 },
        end: { x: 0, y: h },
        colorStops: [
          { offset: 0, color: 0xffe79c },
          { offset: 0.5, color: palette.gold },
          { offset: 1, color: 0xf0a72e },
        ],
        textureSpace: "local",
      });
      this.body.roundRect(0, 0, w, h, 20).fill(grad).stroke({ width: 3, color: 0xfff2c4, alpha: 0.85 });
    } catch {
      this.body.roundRect(0, 0, w, h, 20).fill(palette.gold).stroke({ width: 3, color: 0xfff2c4, alpha: 0.85 });
    }
    this.addChild(this.body);

    // diagonal sheen that sweeps across, clipped to the body
    this.shine.rect(-19, -h * 1.6, 38, h * 3.2).fill({ color: 0xffffff, alpha: 0.6 });
    this.shine.rotation = -0.32;
    this.shine.position.set(0, h / 2);
    this.shine.alpha = 0.16;
    try {
      this.shine.filters = [new BlurFilter({ strength: 5, quality: 2 })];
    } catch {
      /* crisp sheen is an acceptable fallback */
    }
    this.addChild(this.shine);
    this.shineMask.roundRect(0, 0, w, h, 20).fill(0xffffff);
    this.addChild(this.shineMask);
    this.shine.mask = this.shineMask;

    this.labelText = new Text({
      text: "START",
      style: {
        fontFamily: FONT,
        fontWeight: "700",
        fontSize: 46,
        fill: 0xffffff,
        letterSpacing: 5,
        dropShadow: { color: 0x6b3a00, alpha: 0.55, blur: 4, distance: 3, angle: Math.PI / 2 },
      },
    });
    this.labelText.anchor.set(0.5);
    this.labelText.position.set(w / 2, h / 2);
    this.addChild(this.labelText);

    this.eventMode = "static";
    this.cursor = "pointer";
    this.on("pointerover", () => {
      if (!this.enabled) return;
      this.hovered = true;
      scaleTo(this, 1.06, 160, Ease.outBack);
    });
    this.on("pointerout", () => {
      this.hovered = false;
      scaleTo(this, 1, 220, Ease.outCubic);
    });
    this.on("pointertap", () => {
      if (this.enabled) this.onActivate?.();
    });
  }

  setEnabled(on: boolean) {
    this.enabled = on;
    this.alpha = on ? 1 : 0.4;
    this.cursor = on ? "pointer" : "default";
    this.labelText.text = on ? "START" : "PICK ONE";
    if (!on) {
      this.hovered = false;
      scaleTo(this, 1, 200, Ease.outCubic);
    }
  }

  /** Drive the glow pulse + sheen sweep (called from the game ticker on the title screen). */
  tick(clock: number) {
    if (!this.enabled) {
      this.glow.alpha = 0;
      this.shine.alpha = 0;
      return;
    }
    const pulse = 0.5 + 0.5 * Math.sin(clock * 0.005);
    this.glow.alpha = this.hovered ? 0.7 + 0.3 * pulse : 0.28 + 0.12 * pulse;
    const period = this.hovered ? 850 : 2400;
    const t = (clock % period) / period;
    this.shine.x = -this.w * 0.3 + t * (this.w * 1.6);
    this.shine.alpha = this.hovered ? 0.5 : 0.16;
  }
}
